import { useMemo, useState } from 'react';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import {
    useSessionTypes,
    useCreateSessionType,
    useUpdateSessionType,
    useDeleteSessionType,
    type SessionType,
    type SessionTypeCategory,
} from '@/api/sessionTypes';
import { useServiceTypes } from '@/api/serviceTypes';
import { useNoteTypes } from '@/api/noteTypes';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/Badge';
import { FormField, Input } from '@/components/ui/FormField';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/Dialog';
import { cn } from '@/lib/cn';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
type StatusTab = 'Active' | 'Inactive' | 'All';
const STATUS_TABS: StatusTab[] = ['Active', 'Inactive', 'All'];

const CATEGORIES: SessionTypeCategory[] = ['Service', 'Administrative'];

const COLOR_PALETTE: Array<{ value: string; name: string }> = [
    { value: 'rgba(155,190,169,.55)', name: 'Sage' },
    { value: 'rgba(135,175,215,.45)', name: 'Sky' },
    { value: 'rgba(180,160,210,.45)', name: 'Lavender' },
    { value: 'rgba(220,160,180,.45)', name: 'Rose' },
    { value: 'rgba(230,200,125,.50)', name: 'Amber' },
    { value: 'rgba(230,165,125,.45)', name: 'Coral' },
    { value: 'rgba(135,195,195,.50)', name: 'Teal' },
    { value: 'rgba(166,166,166,.40)', name: 'Slate' },
];
const DEFAULT_COLOR = COLOR_PALETTE[0].value;

export function SessionTypesPanel() {
    const { data, isLoading, isError, error } = useSessionTypes();
    const { data: serviceTypes } = useServiceTypes();
    const { data: noteTypes } = useNoteTypes();
    const createM = useCreateSessionType();
    const updateM = useUpdateSessionType();
    const deleteM = useDeleteSessionType();

    const [tab, setTab] = useState<StatusTab>('Active');
    const [editing, setEditing] = useState<SessionType | null>(null);
    const [creating, setCreating] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState<SessionType | null>(null);

    const serviceTypeById = useMemo(() => {
        const m = new Map<string, string>();
        (serviceTypes || []).forEach((s) => m.set(s.id, `${s.code}${s.provider ? ` (${s.provider})` : ''}`));
        return m;
    }, [serviceTypes]);
    const noteTypeById = useMemo(() => {
        const m = new Map<string, string>();
        (noteTypes || []).forEach((n) => m.set(n.id, n.name));
        return m;
    }, [noteTypes]);

    const rows = useMemo(() => {
        if (!data) return [];
        const filtered = tab === 'All' ? data : data.filter((r) => r.status === tab);
        return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    }, [data, tab]);

    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <h2 className="font-display text-lg font-bold text-text">Session Types</h2>
                <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
                    <Plus className="w-3.5 h-3.5" /> Add session type
                </Button>
            </div>
            <p className="text-xs text-text-subtle mb-3">
                What a session looks like on the calendar and in the note. Service sessions are
                linked to a billable service type + note type; administrative sessions
                (trainings, meetings) don't bill or require notes.
            </p>

            <Card className="p-0 overflow-hidden">
                <div className="flex flex-wrap items-center gap-3 p-3 border-b border-border">
                    <div role="tablist" aria-label="Filter session types by status" className="flex items-center gap-1 p-0.5 bg-surface-muted rounded-md">
                        {STATUS_TABS.map((s) => (
                            <button
                                key={s}
                                type="button"
                                role="tab"
                                aria-selected={tab === s}
                                onClick={() => setTab(s)}
                                className={cn(
                                    'px-3 py-1 text-xs rounded transition-colors',
                                    tab === s ? 'bg-surface text-text shadow-xs font-medium' : 'text-text-muted hover:text-text',
                                )}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                    <div className="ml-auto text-xs text-text-subtle tabular-nums">
                        {rows.length} {rows.length === 1 ? 'type' : 'types'}
                    </div>
                </div>

                {isLoading ? (
                    <div className="p-12 flex items-center justify-center text-text-subtle">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                    </div>
                ) : isError ? (
                    <div className="p-12 text-center text-error">
                        <p className="font-medium mb-1">Couldn't load session types</p>
                        <p className="text-xs text-text-subtle">{extractErrorMessage(error, 'Please try again.')}</p>
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-12 text-center text-text-subtle">
                        <p>{data && data.length > 0 ? 'No session types match the current filter.' : 'No session types yet.'}</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                    <th className="px-3 py-2 font-bold">Name</th>
                                    <th className="px-3 py-2 font-bold w-32">Category</th>
                                    <th className="px-3 py-2 font-bold">Service type</th>
                                    <th className="px-3 py-2 font-bold">Note type</th>
                                    <th className="px-3 py-2 font-bold w-20 text-center">Payable</th>
                                    <th className="px-3 py-2 font-bold w-28">Status</th>
                                    <th className="px-3 py-2 font-bold w-24 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => {
                                    const isAdmin = r.category === 'Administrative';
                                    return (
                                        <tr key={r.id} className="border-b border-border last:border-b-0 hover:bg-surface-muted transition-colors">
                                            <td className="px-3 py-2 font-medium">
                                                <div className="flex items-center gap-2">
                                                    {r.color && (
                                                        <span
                                                            className="inline-block w-3 h-3 rounded-sm border border-border/60"
                                                            style={{ backgroundColor: r.color }}
                                                            aria-hidden
                                                        />
                                                    )}
                                                    <span>{r.name}</span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 text-text-muted text-xs">{r.category}</td>
                                            <td className="px-3 py-2 text-text-muted font-mono text-xs">
                                                {isAdmin ? <span className="text-text-subtle">—</span>
                                                    : r.serviceTypeId ? (serviceTypeById.get(r.serviceTypeId) || '—')
                                                    : <span className="text-text-subtle">—</span>}
                                            </td>
                                            <td className="px-3 py-2 text-text-muted text-xs">
                                                {isAdmin ? <span className="text-text-subtle">—</span>
                                                    : r.noteTypeId ? (noteTypeById.get(r.noteTypeId) || '—')
                                                    : <span className="text-text-subtle">—</span>}
                                            </td>
                                            <td className="px-3 py-2 text-center text-xs text-text-muted">
                                                {isAdmin ? <span className="text-text-subtle">—</span> : r.payable ? 'Yes' : 'No'}
                                            </td>
                                            <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                                            <td className="px-3 py-2 text-right">
                                                <Button variant="ghost" size="xs" aria-label={`Edit ${r.name}`} onClick={() => setEditing(r)} className="mr-1">
                                                    <Pencil className="w-3 h-3" />
                                                </Button>
                                                <Button variant="ghost" size="xs" aria-label={`Delete ${r.name}`} onClick={() => setConfirmDelete(r)}>
                                                    <Trash2 className="w-3 h-3" />
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {(creating || editing) && (
                <SessionTypeDialog
                    initial={editing}
                    serviceTypes={serviceTypes || []}
                    noteTypes={noteTypes || []}
                    onClose={() => { setCreating(false); setEditing(null); }}
                    onSubmit={async (payload) => {
                        if (editing) await updateM.mutateAsync({ id: editing.id, payload });
                        else await createM.mutateAsync(payload);
                    }}
                />
            )}

            {confirmDelete && (
                <DeleteConfirmDialog
                    row={confirmDelete}
                    onClose={() => setConfirmDelete(null)}
                    onConfirm={async () => {
                        await deleteM.mutateAsync(confirmDelete.id);
                        setConfirmDelete(null);
                    }}
                />
            )}
        </div>
    );
}

interface DialogPayload {
    name: string;
    color?: string;
    labelDefault?: string;
    category: SessionTypeCategory;
    serviceTypeId: string | null;
    payable: boolean;
    noteTypeId: string | null;
    status: 'Active' | 'Inactive';
}

function SessionTypeDialog({
    initial,
    serviceTypes,
    noteTypes,
    onClose,
    onSubmit,
}: {
    initial: SessionType | null;
    serviceTypes: ReturnType<typeof useServiceTypes>['data'] extends infer T ? Exclude<T, undefined> : never;
    noteTypes: ReturnType<typeof useNoteTypes>['data'] extends infer T ? Exclude<T, undefined> : never;
    onClose: () => void;
    onSubmit: (p: DialogPayload) => Promise<void>;
}) {
    const [name, setName] = useState(initial?.name ?? '');
    const [color, setColor] = useState(initial?.color ?? DEFAULT_COLOR);
    const [labelDefault, setLabelDefault] = useState(initial?.labelDefault ?? '');
    const [category, setCategory] = useState<SessionTypeCategory>(
        (initial?.category === 'Administrative' ? 'Administrative' : 'Service'),
    );
    const [serviceTypeId, setServiceTypeId] = useState<string>(initial?.serviceTypeId ?? '');
    const [payable, setPayable] = useState(initial?.payable ?? true);
    const [noteTypeId, setNoteTypeId] = useState<string>(initial?.noteTypeId ?? '');
    const [status, setStatus] = useState<'Active' | 'Inactive'>(initial?.status ?? 'Active');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const activeServiceTypes = useMemo(
        () => (serviceTypes || []).filter((s) => s.status === 'Active' || s.id === initial?.serviceTypeId),
        [serviceTypes, initial],
    );
    const activeNoteTypes = useMemo(
        () => (noteTypes || []).filter((n) => n.status === 'Active' || n.id === initial?.noteTypeId),
        [noteTypes, initial],
    );

    const isAdmin = category === 'Administrative';

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        if (!name.trim()) { setErr('Name is required'); return; }
        setSaving(true);
        try {
            await onSubmit({
                name: name.trim(),
                color: color || undefined,
                labelDefault: labelDefault.trim() || undefined,
                category,
                serviceTypeId: isAdmin ? null : (serviceTypeId || null),
                payable: isAdmin ? false : payable,
                noteTypeId: isAdmin ? null : (noteTypeId || null),
                status,
            });
            onClose();
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not save.'));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined} className="max-w-[32rem]">
                <DialogHeader>
                    <DialogTitle>{initial ? 'Edit session type' : 'Add session type'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <FormField label="Name" required error={err ?? undefined}>
                        {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
                    </FormField>

                    <div className="grid grid-cols-2 gap-3">
                        <FormField label="Category">
                            {(id) => (
                                <select
                                    id={id}
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value as SessionTypeCategory)}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                                >
                                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            )}
                        </FormField>
                        <FormField label="Calendar color" className="w-max mx-auto">
                            {() => {
                                const inPalette = COLOR_PALETTE.some((c) => c.value === color);
                                return (
                                    <div role="radiogroup" aria-label="Calendar color" className="grid grid-cols-4 gap-1.5 w-max">
                                        {!inPalette && color && (
                                            <button
                                                type="button"
                                                role="radio"
                                                aria-checked
                                                aria-label="Current (custom)"
                                                title="Current (custom)"
                                                className="w-7 h-7 rounded-md border border-border ring-2 ring-accent ring-offset-2 ring-offset-background"
                                                style={{ backgroundColor: color }}
                                            />
                                        )}
                                        {COLOR_PALETTE.map((c) => {
                                            const selected = color === c.value;
                                            return (
                                                <button
                                                    key={c.value}
                                                    type="button"
                                                    role="radio"
                                                    aria-checked={selected}
                                                    aria-label={c.name}
                                                    title={c.name}
                                                    onClick={() => setColor(c.value)}
                                                    className={cn(
                                                        'w-7 h-7 rounded-md border border-border transition-shadow',
                                                        selected && 'ring-2 ring-accent ring-offset-2 ring-offset-background',
                                                    )}
                                                    style={{ backgroundColor: c.value }}
                                                />
                                            );
                                        })}
                                    </div>
                                );
                            }}
                        </FormField>
                    </div>

                    <FormField label="Default label" hint="Optional shorthand shown on calendar blocks">
                        {(id) => <Input id={id} value={labelDefault} onChange={(e) => setLabelDefault(e.target.value)} />}
                    </FormField>

                    {!isAdmin && (
                        <>
                            <FormField label="Service type" hint="Drives the CPT/HCPCS code and rate on claims">
                                {(id) => (
                                    <select
                                        id={id}
                                        value={serviceTypeId}
                                        onChange={(e) => setServiceTypeId(e.target.value)}
                                        className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                                    >
                                        <option value="">— None —</option>
                                        {activeServiceTypes.map((s) => (
                                            <option key={s.id} value={s.id}>
                                                {s.code}{s.provider ? ` (${s.provider})` : ''} — {s.name}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </FormField>

                            <FormField label="Note type">
                                {(id) => (
                                    <select
                                        id={id}
                                        value={noteTypeId}
                                        onChange={(e) => setNoteTypeId(e.target.value)}
                                        className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                                    >
                                        <option value="">— None —</option>
                                        {activeNoteTypes.map((n) => (
                                            <option key={n.id} value={n.id}>{n.name}</option>
                                        ))}
                                    </select>
                                )}
                            </FormField>

                            <label className="flex items-center gap-2 text-sm text-text mt-2 mb-3">
                                <input type="checkbox" checked={payable} onChange={(e) => setPayable(e.target.checked)} className="rounded border-border" />
                                Payable
                            </label>
                        </>
                    )}

                    <FormField label="Status">
                        {(id) => (
                            <select
                                id={id}
                                value={status}
                                onChange={(e) => setStatus(e.target.value as 'Active' | 'Inactive')}
                                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                            >
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                            </select>
                        )}
                    </FormField>

                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="ghost" size="sm" type="button">Cancel</Button>
                        </DialogClose>
                        <Button variant="primary" size="sm" type="submit" disabled={saving}>
                            {saving ? 'Saving…' : initial ? 'Save' : 'Create'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function DeleteConfirmDialog({
    row,
    onClose,
    onConfirm,
}: {
    row: SessionType;
    onClose: () => void;
    onConfirm: () => Promise<void>;
}) {
    const [working, setWorking] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Delete session type?</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-text-muted">
                    Delete <span className="font-medium text-text">{row.name}</span>? If any
                    session references this type the delete will be refused.
                </p>
                {err && <p className="mt-2 text-xs text-error">{err}</p>}
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" type="button">Cancel</Button>
                    </DialogClose>
                    <Button
                        variant="danger"
                        size="sm"
                        disabled={working}
                        onClick={async () => {
                            setErr(null);
                            setWorking(true);
                            try { await onConfirm(); }
                            catch (e) { setErr(extractErrorMessage(e, 'Could not delete.')); }
                            finally { setWorking(false); }
                        }}
                    >
                        {working ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
